import { hashKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import maxBy from 'lodash/maxBy';

import type { SalesforceSubmission } from '~/components/PricingTool/submit/buildSalesforceSubmission';

import OffersService from '~/services/OffersService';

import { offerQueryKeys } from './useGetOffer';

export interface PricingToolSubmission {
  id: string;
  opportunity_id: string;
  status: 'pending' | 'confirmed' | 'failed';
  request_snapshot: SalesforceSubmission;
  salesforce_result: {
    errors?: Array<{ reference_id?: string; error_code?: string; message: string }>;
    // Created asset's draft id -> its Salesforce id.
    asset_ids?: Record<string, string>;
  } | null;
  created_by: string;
  created_at: string;
}

export function useGetLastConfirmedPricingToolSubmission(opportunityId: string | undefined) {
  return useQuery({
    queryKey: offerQueryKeys.pricingToolSubmissions(opportunityId),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getPricingToolSubmissions(opportunityId);
      return response.result as Array<PricingToolSubmission>;
    },
    select: (submissions) =>
      maxBy(
        submissions.filter(({ status }) => status === 'confirmed'),
        'created_at',
      ) ?? null,
    enabled: !!opportunityId,
  });
}

export function useSubmitPricingTool(opportunityId: string | undefined, vendorAccountId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { idempotency_key: string; submission: SalesforceSubmission }) => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.submitPricingTool(opportunityId, body);
      return response.result as PricingToolSubmission;
    },
    onSuccess: () => {
      // Skips the draft: it was saved just before submitting, and a refetch would rehydrate every slice.
      const draftKeyHash = hashKey(offerQueryKeys.pricingToolDraft(opportunityId));
      queryClient.invalidateQueries({
        queryKey: offerQueryKeys.opportunity(opportunityId ?? ''),
        predicate: ({ queryHash }) => queryHash !== draftKeyHash,
      });
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.account(vendorAccountId) });
    },
    onError: () => queryClient.invalidateQueries({ queryKey: offerQueryKeys.pricingToolSubmissions(opportunityId) }),
  });
}
